import type { RowId } from "@/lib/odds-screen/live/types";

export type RowList<R> = {
  readonly ids: readonly RowId[];
  readonly rowAt: (id: RowId) => R | null;
};

export type RowOf<L> = L extends RowList<infer R> ? R : never;

export type HeldOrder<L extends RowList<unknown>> = L & {
  readonly lapsed: ReadonlyMap<RowId, RowOf<L>>;
};

export const ORDER_RELEASE_MS = 1_000;

const NONE_LAPSED = new Map<RowId, never>();

export function holdOrder<L extends RowList<unknown>>(previous: HeldOrder<L> | null, latest: L): HeldOrder<L> {
  if (previous === null) return { ...latest, lapsed: NONE_LAPSED };
  const passing = new Set(latest.ids);
  const lapsed = new Map<RowId, RowOf<L>>();
  for (const id of previous.ids) {
    if (passing.has(id)) continue;
    const row = previous.rowAt(id) as RowOf<L> | null;
    if (row !== null) lapsed.set(id, row);
  }
  return {
    ...latest,
    ids: previous.ids,
    lapsed: lapsed.size === 0 ? NONE_LAPSED : lapsed,
    rowAt: (id: RowId) => latest.rowAt(id) ?? lapsed.get(id) ?? null,
  };
}

export function dropHidden(held: readonly RowId[], before: readonly RowId[], after: readonly RowId[]): readonly RowId[] | null {
  const was = new Set(before);
  if (after.some((id) => !was.has(id))) return null;
  const passing = new Set(after);
  return held.filter((id) => passing.has(id) || !was.has(id));
}
